import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type DbProduct = Tables<'products'>;
export type DbCategory = Tables<'categories'>;
export type DbReview = Tables<'reviews'>;
export type DbBadge = Tables<'badges'>;

export function useBadges() {
  return useQuery({
    queryKey: ['badges'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('badges')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as DbBadge[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as DbCategory[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function useProducts(categorySlug?: string | null) {
  return useQuery({
    queryKey: ['products', categorySlug],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select('*, categories(slug)')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (categorySlug) {
        // First get the parent category
        const { data: parentCat } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', categorySlug)
          .single();

        if (parentCat) {
          // Get subcategories of this parent
          const { data: subCats } = await supabase
            .from('categories')
            .select('id')
            .eq('parent_id', parentCat.id);

          const ids = [parentCat.id, ...(subCats || []).map(c => c.id)];
          query = supabase
            .from('products')
            .select('*, categories(slug)')
            .eq('is_active', true)
            .in('category_id', ids)
            .order('created_at', { ascending: false });
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (DbProduct & { categories: { slug: string } | null })[];
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useProduct(slugOrId: string) {
  return useQuery({
    queryKey: ['product', slugOrId],
    queryFn: async () => {
      const isShortId = /^\d+$/.test(slugOrId);
      let query = supabase.from('products').select('*, categories(slug, name)');
      query = isShortId
        ? query.like('slug', `%-${slugOrId}`)
        : query.eq('slug', slugOrId);
      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Product not found');
      return data as DbProduct & { categories: { slug: string; name: string } | null };
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useProductReviews(productId: string) {
  return useQuery({
    queryKey: ['reviews', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as DbReview[];
    },
    enabled: !!productId,
  });
}

export function useRelatedProducts(categoryId: string | null, currentProductId: string) {
  return useQuery({
    queryKey: ['related-products', categoryId, currentProductId],
    queryFn: async () => {
      if (!categoryId) return [];
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(slug)')
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .neq('id', currentProductId)
        .limit(4);
      if (error) throw error;
      return data as (DbProduct & { categories: { slug: string } | null })[];
    },
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useHomepageProducts() {
  return useQuery({
    queryKey: ['homepage-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(slug)')
        .eq('is_active', true)
        .eq('show_on_homepage', true)
        .order('badge', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(8);
      if (error) throw error;
      return data as (DbProduct & { categories: { slug: string } | null })[];
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useProductsRatings() {
  return useQuery({
    queryKey: ['products-ratings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('product_id, rating');
      if (error) throw error;
      const map = new Map<string, { sum: number; count: number }>();
      (data || []).forEach((r: any) => {
        const cur = map.get(r.product_id) || { sum: 0, count: 0 };
        cur.sum += r.rating;
        cur.count += 1;
        map.set(r.product_id, cur);
      });
      const result: Record<string, { avg: number; count: number }> = {};
      map.forEach((v, k) => { result[k] = { avg: v.sum / v.count, count: v.count }; });
      return result;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useFeaturedProducts(limit?: number) {
  return useQuery({
    queryKey: ['featured-products', limit ?? 'all'],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select('*, categories(slug)')
        .eq('is_active', true)
        .eq('is_featured', true)
        .order('featured_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (limit) query = query.limit(limit);
      const { data, error } = await query;
      if (error) throw error;
      return data as (DbProduct & { categories: { slug: string } | null })[];
    },
    staleTime: 1000 * 60 * 5,
  });
}
